import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createApiInstance } from '../api';
import toastService from '../services/toastService';
import logger from '../utils/logger';
import { LIFETIME_CONTRIBUTION_THRESHOLD } from '../utils/contributionSilence';
import { ACTIVATION_MIN_AMOUNT } from '../utils/membershipAccess';
import { PAYPAL_ENABLED } from '../config/apiConfig';
import './ContributionPopup.css';

const popupApi = createApiInstance();

const MEMBER_STATS_CACHE_TTL_MS = 10 * 60 * 1000;
const PRORATE_PER_MONTH = 10;
const PRORATE_PER_DAY = 1;
const ACTIVATION_TIER_MONTHS = {
  60: 6,
  100: 12,
  150: 18,
  175: 24,
  200: 36,
};

const ContributionPopup = ({ isOpen, onClose, contributionConfig, embedded = false, onSuccess = null }) => {
  const [selectedAmount, setSelectedAmount] = useState(60); // Default to $60
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalFailed, setPaypalFailed] = useState(false);
  const [paypalKey, setPaypalKey] = useState(0);
  const paypalContainerRef = useRef(null);
  const paypalScriptLoaded = useRef(false);
  const amountRef = useRef(60);
  const paypalInitialized = useRef(false);

  // ─── PayPal (direct SDK checkout + PayPal QR) temporarily hidden ──────
  // Reason: PayPal payments were getting stuck (order created/approved but
  // never completing capture on the provider side). All PayPal options —
  // direct "PayPal" button and "PayPal QR" — are gated behind
  // PAYPAL_ENABLED (config/apiConfig.js) and the PayPal SDK load is
  // skipped while the flag is off. Venmo QR and Clover card remain.
  // Default stays 'clover' so the toggle still starts on a working method.
  // To re-enable: set REACT_APP_PAYPAL_ENABLED=true (or flip the flag
  // default) — all gated blocks restore automatically.
  const [paymentMethod, setPaymentMethod] = useState('clover'); // 'clover', 'paypal', 'venmo-qr', 'paypal-qr'
  const [showAltPaymentMethods, setShowAltPaymentMethods] = useState(false); // Always start collapsed
  const [cloverLoading, setCloverLoading] = useState(false);
  const [cloverReady, setCloverReady] = useState(false);
  const [cloverConfig, setCloverConfig] = useState(null);
  const cloverInstanceRef = useRef(null);
  const cloverMountedRef = useRef(false);
  const hasLoggedShownRef = useRef(false);
  const hasLoggedDismissedRef = useRef(false);
  const [cloverSuccess, setCloverSuccess] = useState(false);
  const [cloverRecurring, setCloverRecurring] = useState(false);
  const [memberStats, setMemberStats] = useState({
    daysActive: 0,
    profileViews: 0,
    profileFavorites: 0,
    profileShortlists: 0,
    conversations: 0,
  });
  const [memberStatsLoading, setMemberStatsLoading] = useState(false);
  const [contributionStatus, setContributionStatus] = useState(null);
  const [showTierInfo, setShowTierInfo] = useState(false);

  const loadMemberStats = useCallback(async () => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    if (!token || !username) return;

    const cacheKey = `contribution_member_stats_v3:${username}`;
    try {
      const cachedRaw = sessionStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (
          cached &&
          cached.cachedAt &&
          Date.now() - Number(cached.cachedAt) < MEMBER_STATS_CACHE_TTL_MS &&
          cached.stats
        ) {
          setMemberStats(cached.stats);
          return;
        }
      }
    } catch (err) {
      logger.debug('Contribution popup stats cache parse failed', err);
    }

    setMemberStatsLoading(true);
    try {
      const statsRes = await popupApi.get(`/api/users/${encodeURIComponent(username)}/stats`);
      const statsData = statsRes.data;
      const stats = statsData?.stats || {};

      const nextStats = {
        daysActive: stats.daysActive || 0,
        profileViews: stats.lifetimeViews || stats.profileViews || 0,
        profileFavorites: stats.lifetimeFavorites || stats.favoritedBy || 0,
        profileShortlists: stats.lifetimeShortlists || stats.shortlistedBy || 0,
        conversations: stats.lifetimeMessages || stats.uniqueConversations || 0,
      };

      setMemberStats(nextStats);
      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({
            cachedAt: Date.now(),
            stats: nextStats,
          })
        );
      } catch (err) {
        logger.debug('Contribution popup stats cache write failed', err);
      }
    } catch (err) {
      logger.warn('Failed to load contribution member stats', err);
    } finally {
      setMemberStatsLoading(false);
    }
  }, []);

  const loadContributionStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    if (!token || !username) return;

    try {
      const response = await popupApi.get('/api/contributions/contribution-status');
      const data = response.data;
      setContributionStatus(data);
      logger.debug('Contribution status loaded:', data);
    } catch (err) {
      logger.warn('Failed to load contribution status', err);
    }
  }, []);

  // Membership prompt tiers (optimized to appeal while preserving min $60).
  // After activation (hasAccess), keep donation options at $100 and $30.
  const rawAmounts = [100, 60];
  const amounts = rawAmounts
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0)
    .sort((a, b) => b - a);

  // For donations (after membership), only show $100, $60, and custom
  const hasMembershipAccess = Boolean(contributionStatus?.membership?.hasAccess);
  const activationFlow = !hasMembershipAccess;
  const donationAmounts = hasMembershipAccess ? [100, 60] : amounts;
  const getActivationMonthsLabel = useCallback((amount) => {
    const numericAmount = Number(amount);
    const tierMonths = ACTIVATION_TIER_MONTHS[numericAmount];
    if (tierMonths) return `${tierMonths} months`;

    // For amounts < $60, use $1/day scale
    // For amounts >= $60, use $10/month scale
    if (numericAmount < 60) {
      const days = Math.floor(numericAmount / PRORATE_PER_DAY);
      return `${days} days`;
    } else {
      const proratedMonths = Math.floor(numericAmount / PRORATE_PER_MONTH);
      return `${proratedMonths} months`;
    }
  }, []);

  useEffect(() => {
    // Don't force Clover-only mode - allow all payment methods in both flows
    // Users can choose their preferred payment method
  }, []);

  // Build engagement metrics list: numeric values as colored pills, or "no ... yet" if all zero.
  const engagementMetrics = (() => {
    const metrics = [];
    if (memberStats.profileFavorites > 0) {
      metrics.push({ value: memberStats.profileFavorites, label: 'favorites', className: 'metric-favorites' });
    }
    if (memberStats.profileShortlists > 0) {
      metrics.push({ value: memberStats.profileShortlists, label: 'shortlists', className: 'metric-shortlists' });
    }
    if (memberStats.conversations > 0) {
      metrics.push({ value: memberStats.conversations, label: 'messages', className: 'metric-messages' });
    }

    if (metrics.length === 0) {
      return 'no favorites, shortlists, or messages yet';
    }

    return metrics.map((m, i) => {
      const isFirst = i === 0;
      const isLast = i === metrics.length - 1;
      const connector = isFirst ? '' : (isLast ? ', and ' : ', ');
      return (
        <React.Fragment key={m.label}>
          {connector}
          <span className={`contribution-metric-pill ${m.className}`}>{m.value} {m.label}</span>
        </React.Fragment>
      );
    });
  })();

  // Log activity to backend (fire and forget)
  const logActivity = useCallback(async (action, amount = null, pType = null) => {
    try {
      await popupApi.post('/api/contributions/log-contribution-activity', {
        action,
        amount,
        paymentType: pType
      });
    } catch (err) {
      // Silent fail - don't disrupt user experience
    }
  }, []);

  // Get the current amount
  const getAmount = useCallback(() => {
    if (selectedAmount === 'custom') {
      return parseFloat(customAmount) || 0;
    }
    return parseFloat(selectedAmount) || 0;
  }, [selectedAmount, customAmount]);

  // Load PayPal SDK script
  const loadPayPalScript = useCallback(async () => {
    if (paypalScriptLoaded.current || window.paypal) {
      paypalScriptLoaded.current = true;
      return true;
    }

    try {
      const response = await popupApi.get('/api/paypal/config');
      const config = response.data;

      if (!config.configured || !config.client_id) {
        setPaypalFailed(true);
        setError('PayPal is not configured. Please contact support.');
        return false;
      }

      return new Promise((resolve) => {
        const script = document.createElement('script');
        // Only disable the legacy PayPal Credit funding source. Keep 'card'
        // so the "Debit or Credit Card" button shows alongside PayPal.
        script.src = `https://www.paypal.com/sdk/js?client-id=${config.client_id}&currency=USD&disable-funding=credit`;
        script.async = true;
        script.onload = () => {
          paypalScriptLoaded.current = true;
          resolve(true);
        };
        script.onerror = () => {
          setPaypalFailed(true);
          setError('PayPal failed to load. Please disable ad blockers and try again.');
          resolve(false);
        };
        document.body.appendChild(script);
      });
    } catch (err) {
      setPaypalFailed(true);
      setError('Failed to initialize PayPal.');
      return false;
    }
  }, []);

  // Render PayPal buttons into the current container
  const renderPayPalButtons = useCallback(() => {
    if (!window.paypal || !paypalContainerRef.current) return;

    try {
      window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'paypal',
          height: 40,
          tagline: false
        },
        createOrder: async () => {
          const amount = amountRef.current;
          const minimumAmount = activationFlow ? ACTIVATION_MIN_AMOUNT : 1;
          if (!amount || amount < minimumAmount) {
            setError(`Please select a valid amount (minimum $${minimumAmount})`);
            throw new Error('Invalid amount');
          }

          const response = await popupApi.post('/api/paypal/create-order', {
            amount: amount.toFixed(2),
            currency: 'USD',
            plan_id: 'contribution',
            description: `Contribution - $${amount.toFixed(2)}`
          });
          const data = response.data;
          if (data.order_id) {
            logActivity('proceed_to_payment', amount, 'one-time');
            return data.order_id;
          }
          throw new Error(data.detail || 'Failed to create order');
        },
        onApprove: async (data) => {
          setLoading(true);
          setError('');
          try {
            const response = await popupApi.post('/api/paypal/capture-order', { order_id: data.orderID });

            const captureData = response.data;
            if (captureData.success) {
              logActivity('contributed', amountRef.current, 'one-time');
              // Notify the hook so banners refresh (silence window now active).
              window.dispatchEvent(new Event('contributionMade'));
              if (toastService) {
                toastService.success('Thank you for your contribution! 💜');
              }
              onClose();
            } else {
              setError(captureData.detail || 'Payment failed. Please try again.');
            }
          } catch (err) {
            setError('Payment failed. Please try again.');
          } finally {
            setLoading(false);
          }
        },
        onCancel: () => {
          logActivity('payment_cancelled');
        },
        onError: (err) => {
          setError('PayPal encountered an error. Please try Clover card payment instead.');
        }
      }).render(paypalContainerRef.current)
        .then(() => {
          setPaypalReady(true);
        })
        .catch((renderErr) => {
          // Suppress 'container removed from DOM' errors when popup closes
          const msg = renderErr?.message || String(renderErr);
          if (!msg.includes('container') && !msg.includes('removed') && !msg.includes('DOM')) {
            setPaypalFailed(true);
          }
        });
    } catch (err) {
      setPaypalFailed(true);
    }
  }, [logActivity, onClose, activationFlow]);

  // Keep amountRef in sync and debounce paypalKey bump to avoid duplicate buttons
  useEffect(() => {
    const newAmount = getAmount();
    amountRef.current = newAmount;

    if (!paypalInitialized.current) return;

    // Debounce for custom input (user typing), instant for preset amounts
    const delay = selectedAmount === 'custom' ? 800 : 50;
    const timer = setTimeout(() => {
      setPaypalKey(k => k + 1);
      setPaypalReady(false);
    }, delay);

    return () => clearTimeout(timer);
  }, [getAmount, selectedAmount]);

  // Reset PayPal ready state when leaving the PayPal tab so it re-renders on return
  useEffect(() => {
    if (paymentMethod !== 'paypal') {
      setPaypalReady(false);
    }
  }, [paymentMethod]);

  // Re-render PayPal buttons when container is available (tab switch or amount change)
  useEffect(() => {
    if (isOpen && paypalScriptLoaded.current && window.paypal && paypalContainerRef.current && !paypalReady) {
      renderPayPalButtons();
    }
  }, [paypalKey, isOpen, paypalReady, paymentMethod, renderPayPalButtons]);

  // Handle dismiss. onClose() delegates to the hook, which marks the popup
  // dismissed for this session (suppresses system-initiated re-opens).
  const handleDismiss = useCallback(() => {
    if (!loading && !hasLoggedDismissedRef.current) {
      hasLoggedDismissedRef.current = true;
      logActivity('popup_dismissed');
      onClose();
    }
  }, [loading, logActivity, onClose]);

  // Handle body scroll lock when popup opens/closes
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      hasLoggedDismissedRef.current = false;
      if (!hasLoggedShownRef.current) {
        hasLoggedShownRef.current = true;
        logActivity('popup_shown');
      }
      loadMemberStats();
      loadContributionStatus();
    } else {
      document.body.style.overflow = 'unset';
      hasLoggedShownRef.current = false;
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, logActivity, loadMemberStats, loadContributionStatus]);

  // Load PayPal SDK once and render buttons on first open (skipped while inactive)
  useEffect(() => {
    if (isOpen && PAYPAL_ENABLED && !paypalInitialized.current && !paypalFailed) {
      const initPayPal = async () => {
        const loaded = await loadPayPalScript();
        if (loaded) {
          paypalInitialized.current = true;
          setTimeout(() => renderPayPalButtons(), 200);
        }
      };
      initPayPal();
    }
  }, [isOpen, paypalFailed, loadPayPalScript, renderPayPalButtons]);

  // Initialize Clover SDK when Card tab is selected
  useEffect(() => {
    if (!isOpen || paymentMethod !== 'clover') return;
    if (cloverMountedRef.current) return;

    const initClover = async () => {
      try {
        if (!window.isSecureContext) {
          setError('Card payments require HTTPS. Please try the Venmo QR option instead.');
          return;
        }

        // Fetch SDK config from backend
        const res = await popupApi.get('/api/clover/sdk-config').catch(err => {
          const config = err.response?.data;
          logger.error('Clover SDK config fetch failed:', config);
          setError(config?.detail || 'Clover card payments not available.');
          return null;
        });
        if (!res) return;
        const config = res.data;
        if (!config.public_key) {
          logger.error('Clover config missing public_key:', config);
          setError('Clover card payments not available.');
          return;
        }
        logger.info('Clover SDK config loaded:', { public_key: config.public_key, sdk_url: config.sdk_url });
        setCloverConfig(config);

        // Load Clover SDK script and wait for the global to be available
        if (!window.Clover) {
          const existing = document.querySelector(`script[src="${config.sdk_url}"]`);
          if (!existing) {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = config.sdk_url;
              script.async = true;
              script.onload = () => {
                logger.info('Clover SDK script loaded');
                resolve();
              };
              script.onerror = () => {
                logger.error('Failed to load Clover SDK script');
                reject(new Error('Failed to load Clover SDK script'));
              };
              document.head.appendChild(script);
            });
          }

          // Poll for window.Clover to become available (up to 5 seconds)
          let attempts = 0;
          while (!window.Clover && attempts < 50) {
            await new Promise(r => setTimeout(r, 100));
            attempts++;
          }
        }

        // Verify Clover SDK is available
        if (!window.Clover) {
          logger.error('window.Clover is undefined after script load. sdk_url:', config.sdk_url);
          setError('Clover SDK failed to load. Please try the Venmo QR option instead.');
          return;
        }

        // Initialize Clover instance with merchantId (required by SDK)
        try {
          const isProduction = config.environment === 'production';
          const cloverOpts = {
            merchantId: config.merchant_id,
            environment: config.environment,
            baseUrl: isProduction ? 'https://token.clover.com' : 'https://token-sandbox.dev.clover.com',
            targetOrigin: isProduction ? 'https://checkout.clover.com' : 'https://checkout.sandbox.dev.clover.com',
            cors: true
          };
          const clover = new window.Clover(config.public_key, cloverOpts);
          cloverInstanceRef.current = clover;
          const elements = clover.elements();
          logger.info('Clover instance initialized');

          // Mount card elements into DOM containers
          const styles = {
            body: { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', fontSize: '14px' },
            input: { fontSize: '15px', padding: '10px 8px' }
          };
          const cardNumber = elements.create('CARD_NUMBER', styles);
          const cardDate = elements.create('CARD_DATE', styles);
          const cardCvv = elements.create('CARD_CVV', styles);
          const cardPostalCode = elements.create('CARD_POSTAL_CODE', styles);

          // Small delay to ensure DOM containers are rendered
          setTimeout(() => {
            try {
              // Check if DOM elements exist before mounting
              const cardNumberEl = document.querySelector('#clover-card-number');
              const cardDateEl = document.querySelector('#clover-card-date');
              const cardCvvEl = document.querySelector('#clover-card-cvv');
              const cardZipEl = document.querySelector('#clover-card-zip');

              if (!cardNumberEl || !cardDateEl || !cardCvvEl || !cardZipEl) {
                setError('Card form containers not found. Please try again.');
                return;
              }

              cardNumber.mount('#clover-card-number');
              cardDate.mount('#clover-card-date');
              cardCvv.mount('#clover-card-cvv');
              cardPostalCode.mount('#clover-card-zip');
              cloverMountedRef.current = true;
              setCloverReady(true);
            } catch (mountErr) {
              logger.error('Clover mount error:', mountErr);
              setError(`Failed to mount card form: ${mountErr.message || 'Unknown error'}`);
            }
          }, 300);
        } catch (initErr) {
          logger.error('Clover initialization error:', initErr);
          setError(`Clover initialization failed: ${initErr.message || 'Invalid API key or configuration'}. Please try the Venmo QR option instead.`);
          return;
        }
      } catch (err) {
        logger.error('Clover setup error:', err);
        setError('Failed to initialize card payment form. Please try the Venmo QR option instead.');
      }
    };
    initClover().catch(err => {
      logger.error('Clover initialization failed:', err);
      setError('Failed to initialize card payment form. Please try the Venmo QR option instead.');
    });

    return () => {
      cloverMountedRef.current = false;
      setCloverReady(false);
    };
  }, [isOpen, paymentMethod, embedded]);

  // Handle Clover card payment submission
  const handleCloverPay = useCallback(async () => {
    if (!cloverInstanceRef.current) return;
    const amount = getAmount();
    const minimumAmount = activationFlow ? ACTIVATION_MIN_AMOUNT : 1;
    if (!amount || amount < minimumAmount) {
      setError(`Please select a valid amount (minimum $${minimumAmount})`);
      return;
    }
    setCloverLoading(true);
    setError('');
    try {
      // Small delay to ensure all fields are properly validated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const result = await cloverInstanceRef.current.createToken();
      logger.debug('Clover createToken result:', result);
      if (result.errors) {
        const errMsgs = Object.values(result.errors).map(e => typeof e === 'string' ? e : (e?.message || JSON.stringify(e)));
        logger.error('Clover validation errors:', result.errors);
        setError(`${errMsgs.join(', ')}. Please try the Venmo QR option if card payment continues to fail.`);
        setCloverLoading(false);
        return;
      }
      const sourceToken = result.token;
      logActivity('proceed_to_payment', amount, 'clover-card');

      // Send token to backend to create charge
      const chargeRes = await popupApi.post('/api/clover/charge', {
        source: sourceToken,
        amount: amount.toFixed(2),
        description: `Contribution - $${amount.toFixed(2)}`,
        recurring: cloverRecurring
      });

      const chargeData = chargeRes.data;
      if (chargeData.success) {
        setCloverSuccess(true);
        // Notify the hook so banners refresh (silence window now active).
        window.dispatchEvent(new Event('contributionMade'));
        if (toastService) {
          const recurMsg = cloverRecurring ? ' (monthly recurring)' : '';
          toastService.success(`Payment of $${amount.toFixed(2)}${recurMsg} successful! Thank you!`);
        }
        if (embedded && onSuccess) {
          onSuccess();
        } else {
          setTimeout(() => onClose(), 2500);
        }
      } else {
        const detail = chargeData.detail;
        const msg = typeof detail === 'string' ? detail
          : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
          : (detail?.msg || 'Card charge failed. Please try the Venmo QR option instead.');
        setError(msg);
      }
    } catch (err) {
      logger.error('Clover payment error:', err);
      setError('Failed to process card payment. Please try the Venmo QR option instead.');
    } finally {
      setCloverLoading(false);
    }
  }, [getAmount, logActivity, onClose, onSuccess, embedded, cloverRecurring, activationFlow]);

  // ESC key handler
  useEffect(() => {
    if (embedded) return;

    const handleEscKey = (event) => {
      if (event.key === 'Escape' && !loading) {
        handleDismiss();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, loading, handleDismiss, embedded]);

  const isLifetimeSupporter = Number(contributionStatus?.lastContributionAmount) >= LIFETIME_CONTRIBUTION_THRESHOLD;

  if (embedded) {
    return (
      <div className="contribution-form-embedded">
        <div className="contribution-popup-body">
          {isLifetimeSupporter ? (
            <div className="contribution-lifetime-view">
              <span className="contribution-lifetime-icon" aria-hidden="true">✨</span>
              <h3 className="contribution-lifetime-title">You're a Lifetime Supporter</h3>
              <p className="contribution-lifetime-message">
                {`Thank you for your $${LIFETIME_CONTRIBUTION_THRESHOLD} contribution. You'll never see this popup again.`}
              </p>
            </div>
          ) : (
            <>
              {/* Thank you message for members */}
              {contributionStatus?.membership?.hasAccess && (
                <div className="member-thank-you">
                  <span className="thank-you-icon">💝</span>
                  <span className="thank-you-text">Thank you for being a member! Your support helps us continue providing quality service.</span>
                </div>
              )}

              <p className="contribution-message">
                {memberStatsLoading
                  ? 'You’ve been part of L3V3L Matches. Behind the scenes, our admins provide real human help, quick responses, and a premium-grade application with features that go beyond commercial matrimonial sites. If you value this community and want to help us grow, we kindly invite you to contribute. Your support keeps the platform running and helps us build new features.'
                  : (
                    <>
                      You’ve been part of L3V3L Matches for{' '}
                      <span className="contribution-metric-pill metric-days">{memberStats.daysActive} days</span>. So far, your profile has had{' '}
                      <span className="contribution-metric-pill metric-views">{memberStats.profileViews} views</span>, and {engagementMetrics}. 
                      Behind the scenes, our admins provide real human help, quick responses, and a premium-grade application with features that go beyond commercial matrimonial sites.<br />
                      If you value this community and want to help us grow, we kindly invite you to <span className="contribution-metric-pill metric-views">contribute</span>. Your support keeps the platform running and helps us build new features.
                    </>
                  )}
              </p>
              {contributionStatus?.membership && (
                <div className="contribution-debug-line">
                  📊 YTD contributions (current year): ${Number(contributionStatus.membership.ytdPaid || 0).toFixed(2)}
                </div>
              )}

              {error && (
                <div className="contribution-error">
                  {error}
                  {/* "Try PayPal Instead" removed — PayPal is hidden while
                      payments are stuck on the provider side. */}
                  {paymentMethod === 'paypal' && (
                    <button
                      className="try-clover-btn"
                      onClick={() => setPaymentMethod('clover')}
                    >
                      Try Card Payment Instead
                    </button>
                  )}
                </div>
              )}

              <section className="contribution-block contribution-amounts-block" aria-label="Contribution amount options">
                <div className="contribution-block-title">{hasMembershipAccess ? 'Support L3V3L Matches' : 'Choose Your Contribution'}</div>
                <div className="contribution-amounts">
                  {donationAmounts.map((amt) => (
                    <label
                      key={amt}
                      className={`contribution-amount-option ${(!hasMembershipAccess && amt >= ACTIVATION_MIN_AMOUNT) ? 'membership-amount-option' : ''} ${selectedAmount === amt ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="contributionAmount"
                        value={amt}
                        checked={selectedAmount === amt}
                        onChange={() => {
                          setSelectedAmount(amt);
                          setCustomAmount('');
                          setError('');
                          setShowTierInfo(false);
                        }}
                        disabled={loading}
                      />
                      <span className="contribution-amount-label">${amt}</span>
                      {amt === 100 && <span className="heart-badge">❤️</span>}
                      <span className="contribution-months-text">FOR {amt === 100 ? '12' : amt === 60 ? '6' : '1'} MONTHS</span>
                    </label>
                  ))}

                  <label
                    className={`contribution-amount-option custom-option ${selectedAmount === 'custom' ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="contributionAmount"
                      value="custom"
                      checked={selectedAmount === 'custom'}
                      onChange={() => setSelectedAmount('custom')}
                      disabled={loading}
                    />
                    <span className="contribution-amount-label custom-amount-label">
                      $
                      <input
                        type="number"
                        className="custom-amount-input"
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value);
                          setSelectedAmount('custom');
                          setError('');
                          setShowTierInfo(false);
                        }}
                        onFocus={() => {
                          setSelectedAmount('custom');
                          setError('');
                        }}
                        placeholder="Amount"
                        min="1"
                        disabled={loading}
                      />
                    </span>
                  </label>
                </div>
              </section>

              {/* Payment Method Selection */}
              <section className="contribution-block contribution-payment-block" aria-label="Payment method selection">
                <div className="contribution-block-title">Select Payment Method</div>
                <div className="payment-method-toggle activation-inline">
                  <button
                    className={`payment-method-btn ${paymentMethod === 'clover' ? 'active' : ''}`}
                    onClick={() => setPaymentMethod('clover')}
                    disabled={loading || cloverLoading}
                  >
                    <span className="clover-icon">☘</span>
                    Card
                  </button>
                  {showAltPaymentMethods && (
                    <>
                      {/* PayPal (direct SDK checkout) hidden — payments are
                          currently stuck on the provider side. PayPal QR
                          hidden behind PAYPAL_ENABLED while integration is
                          inactive (see apiConfig.js). */}
                      <button
                        className={`payment-method-btn ${paymentMethod === 'venmo-qr' ? 'active' : ''}`}
                        onClick={() => setPaymentMethod('venmo-qr')}
                        disabled={loading}
                      >
                        <span className="venmo-v">V</span>
                        Venmo QR
                      </button>
                      {PAYPAL_ENABLED && (
                        <button
                          className={`payment-method-btn ${paymentMethod === 'paypal-qr' ? 'active' : ''}`}
                          onClick={() => setPaymentMethod('paypal-qr')}
                          disabled={loading}
                        >
                          <span className="paypal-p">P</span>
                          PayPal QR
                        </button>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    className={`payment-expand-btn ${showAltPaymentMethods ? 'open' : ''}`}
                    onClick={() => setShowAltPaymentMethods((prev) => !prev)}
                    disabled={loading}
                    aria-expanded={showAltPaymentMethods}
                    aria-label={showAltPaymentMethods ? 'Hide other payment methods' : 'Show more payment methods'}
                  >
                    {showAltPaymentMethods ? '▾' : '▸'}
                    <span className="payment-expand-text">{showAltPaymentMethods ? 'less' : 'more'}</span>
                  </button>
                </div>
              </section>

              {/* PayPal Buttons */}
              {paymentMethod === 'paypal' && (
                <div className="contribution-paypal-section">
                  {loading && (
                    <div className="paypal-processing">
                      <span className="spinner"></span>
                      Processing payment...
                    </div>
                  )}
                  
                  {!paypalReady && !paypalFailed && (
                    <div className="paypal-loading">
                      <span className="spinner"></span>
                      Loading PayPal...
                    </div>
                  )}

                  <div 
                    key={paypalKey}
                    ref={paypalContainerRef} 
                    id="contribution-paypal-buttons"
                    className={paypalFailed ? 'cp-hidden' : ''}
                  />

                  {paypalFailed && (
                    <div className="paypal-fallback">
                      <p>PayPal is currently unavailable.</p>
                      <button 
                        className="contribution-proceed-btn"
                        onClick={() => {
                          setPaypalFailed(false);
                          setError('');
                          loadPayPalScript().then((loaded) => {
                            if (loaded) renderPayPalButtons();
                          });
                        }}
                      >
                        Retry PayPal
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Clover Card Payment (iframe SDK) */}
              {paymentMethod === 'clover' && (
                <div className="clover-checkout-section">
                  {cloverSuccess ? (
                    <div className="clover-success-msg">
                      <span className="clover-success-icon">✓</span>
                      <p>Payment successful! Thank you for your contribution.</p>
                    </div>
                  ) : (
                    <>
                      <div className="clover-info">
                        <p>Pay securely with credit or debit card.</p>
                      </div>
                      <div className="clover-card-form">
                        <div className="clover-field">
                          <label className="clover-label">Card Number</label>
                          <div id="clover-card-number" className="clover-input-container"></div>
                        </div>
                        <div className="clover-field-row">
                          <div className="clover-field clover-field-half">
                            <label className="clover-label">Expiry</label>
                            <div id="clover-card-date" className="clover-input-container"></div>
                          </div>
                          <div className="clover-field clover-field-half">
                            <label className="clover-label">CVV</label>
                            <div id="clover-card-cvv" className="clover-input-container"></div>
                          </div>
                        </div>
                        <div className="clover-field">
                          <label className="clover-label">ZIP Code</label>
                          <div id="clover-card-zip" className="clover-input-container"></div>
                        </div>
                      </div>
                      {!activationFlow && (
                        <div
                          className={`clover-recurring-toggle ${cloverRecurring ? 'active' : ''}`}
                          onClick={() => setCloverRecurring(prev => !prev)}
                        >
                          <div className={`clover-recurring-switch ${cloverRecurring ? 'on' : ''}`} />
                          <div className="clover-recurring-label">
                            <span>Monthly recurring</span>
                            <span>{cloverRecurring ? `$${getAmount().toFixed(2)}/month auto-charge` : 'One-time payment'}</span>
                          </div>
                        </div>
                      )}
                      <button
                        className="contribution-proceed-btn clover-pay-btn"
                        onClick={handleCloverPay}
                        disabled={cloverLoading || !cloverReady || loading}
                      >
                        {cloverLoading ? (
                          <><span className="spinner"></span> Processing...</>
                        ) : !cloverReady ? (
                          <><span className="spinner"></span> Loading card form...</>
                        ) : (
                          <>{cloverRecurring ? `Pay $${getAmount().toFixed(2)}/mo` : `Pay $${getAmount().toFixed(2)}`}</>
                        )}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Venmo QR Code */}
              {paymentMethod === 'venmo-qr' && (
                <div className="qr-code-section">
                  <div>
                    <div className="qr-code-header">
                      <span className="venmo-v">V</span>
                      <h3>Scan with Venmo</h3>
                    </div>
                    <div className="qr-code-image">
                      <img 
                        src="/images/VenmoQR.png" 
                        alt="Venmo QR Code"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'block';
                        }}
                      />
                      <div className="qr-code-fallback">
                        <p>📱 Venmo QR Code</p>
                        <p className="qr-username">@username</p>
                        <p className="qr-amount">${getAmount().toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="qr-code-instructions">
                      <p>1. Open Venmo app</p>
                      <p>2. Scan this QR code</p>
                      <p>3. Send ${getAmount().toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* PayPal QR Code */}
              {paymentMethod === 'paypal-qr' && (
                <div className="qr-code-section">
                  <div>
                    <div className="qr-code-header">
                      <span className="paypal-p">P</span>
                      <h3>Scan with PayPal</h3>
                    </div>
                    <div className="qr-code-image">
                      <img 
                        src="/images/PaypalQR.png" 
                        alt="PayPal QR Code"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'block';
                        }}
                      />
                      <div className="qr-code-fallback">
                        <p>💳 PayPal QR Code</p>
                        <p className="qr-username">@username</p>
                        <p className="qr-amount">${getAmount().toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="qr-code-instructions">
                      <p>1. Open PayPal app</p>
                      <p>2. Scan this QR code</p>
                      <p>3. Send ${getAmount().toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div onClick={(e) => {
      if (isLifetimeSupporter) {
        onClose();
        return;
      }
      handleDismiss();
    }} className={`contribution-popup-overlay${isOpen ? '' : ' cp-hidden'}`}>
      <div className="contribution-popup" onClick={(e) => e.stopPropagation()}>
        <div className="contribution-popup-body">
          {isLifetimeSupporter && (
            <div className="contribution-lifetime-view">
              <span className="contribution-lifetime-icon" aria-hidden="true">✨</span>
              <h3 className="contribution-lifetime-title">You're a Lifetime Supporter</h3>
              <p className="contribution-lifetime-message">
                {`Thank you for your $${LIFETIME_CONTRIBUTION_THRESHOLD} contribution. You'll never see this popup again.`}
              </p>
              <button className="contribution-remind-btn" onClick={onClose}>
                Close
              </button>
            </div>
          )}
          {!isLifetimeSupporter && (
            <>
              {/* Thank you message for members */}
              {contributionStatus?.membership?.hasAccess && (
                <div className="member-thank-you">
                  <span className="thank-you-icon">💝</span>
                  <span className="thank-you-text">Thank you for being a member! Your support helps us continue providing quality service.</span>
                </div>
              )}

          <p className="contribution-message">
            {memberStatsLoading
              ? 'You’ve been part of L3V3L Matches. Behind the scenes, our admins provide real human help, quick responses, and a premium-grade application with features that go beyond commercial matrimonial sites. If you value this community and want to help us grow, we kindly invite you to contribute. Your support keeps the platform running and helps us build new features.'
              : (
                <>
                  You’ve been part of L3V3L Matches for{' '}
                  <span className="contribution-metric-pill metric-days">{memberStats.daysActive} days</span>. So far, your profile has had{' '}
                  <span className="contribution-metric-pill metric-views">{memberStats.profileViews} views</span>, and {engagementMetrics}. 
                  Behind the scenes, our admins provide real human help, quick responses, and a premium-grade application with features that go beyond commercial matrimonial sites.<br />
                  If you value this community and want to help us grow, we kindly invite you to <span className="contribution-metric-pill metric-views">contribute</span>. Your support keeps the platform running and helps us build new features.
                </>
              )}
          </p>
          {contributionStatus?.membership && (
            <div className="contribution-debug-line">
              📊 YTD contributions (current year): ${Number(contributionStatus.membership.ytdPaid || 0).toFixed(2)}
            </div>
          )}

          {error && (
            <div className="contribution-error">
              {error}
              {/* "Try PayPal Instead" removed — PayPal is hidden while
                  payments are stuck on the provider side. */}
              {paymentMethod === 'paypal' && (
                <button
                  className="try-clover-btn"
                  onClick={() => setPaymentMethod('clover')}
                >
                  Try Card Payment Instead
                </button>
              )}
            </div>
          )}

          <section className="contribution-block contribution-amounts-block" aria-label="Contribution amount options">
            <div className="contribution-block-title">{hasMembershipAccess ? 'Support L3V3L Matches' : 'Choose Your Contribution'}</div>
            <div className="contribution-amounts">
              {donationAmounts.map((amt) => (
                <label
                  key={amt}
                  className={`contribution-amount-option ${(!hasMembershipAccess && amt >= ACTIVATION_MIN_AMOUNT) ? 'membership-amount-option' : ''} ${selectedAmount === amt ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="contributionAmount"
                    value={amt}
                    checked={selectedAmount === amt}
                    onChange={() => {
                      setSelectedAmount(amt);
                      setCustomAmount('');
                      setError('');
                      setShowTierInfo(false);
                    }}
                    disabled={loading}
                  />
                  <span className="contribution-amount-label">${amt}</span>
                      {!hasMembershipAccess && amt >= ACTIVATION_MIN_AMOUNT && (
                        <span className="contribution-amount-micro membership-benefit-micro">{getActivationMonthsLabel(amt)}</span>
                      )}
                  {amt === 100 && <span className="heart-badge">❤️</span>}
                </label>
              ))}

              <label
                className={`contribution-amount-option custom-option ${selectedAmount === 'custom' ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="contributionAmount"
                  value="custom"
                  checked={selectedAmount === 'custom'}
                  onChange={() => setSelectedAmount('custom')}
                  disabled={loading}
                />
                <span className="contribution-amount-label custom-amount-label">
                  $
                  <input
                    type="number"
                    className="custom-amount-input"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount('custom');
                      setError('');
                      setShowTierInfo(false);
                    }}
                    onFocus={() => {
                      setSelectedAmount('custom');
                      setError('');
                    }}
                    placeholder="Amount"
                    min="1"
                    disabled={loading}
                  />
                  {!hasMembershipAccess && (
                    <button
                      type="button"
                      className={`tier-info-trigger ${showTierInfo ? 'open' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedAmount('custom');
                        setShowTierInfo((prev) => !prev);
                      }}
                      aria-label="Show bonus tier months"
                      title="Show bonus tier months"
                    >
                      ?
                    </button>
                  )}
                </span>
                {!hasMembershipAccess && !showTierInfo && (
                  <span className="contribution-amount-micro contribution-custom-micro">
                    {selectedAmount === 'custom' && customAmount && Number(customAmount) < 60
                      ? `🙏 $${PRORATE_PER_DAY}/day (minimum ${ACTIVATION_MIN_AMOUNT} for tier benefits)`
                      : `🙏 Prorated at $${PRORATE_PER_MONTH}/month (minimum ${ACTIVATION_MIN_AMOUNT})`
                    }
                  </span>
                )}
                {!hasMembershipAccess && showTierInfo && (
                  <div className="tier-info-popover" onClick={(e) => e.stopPropagation()}>
                    <div className="tier-info-title">Bonus Tiers</div>
                    <div className="tier-info-row"><span>$150</span><span>18 months</span></div>
                    <div className="tier-info-row"><span>$175</span><span>24 months</span></div>
                    <div className="tier-info-row"><span>$200+</span><span>36 months</span></div>
                  </div>
                )}
              </label>
            </div>
          </section>

          {/* Payment Method Selection */}
          <section className="contribution-block contribution-payment-block" aria-label="Payment method selection">
            <div className="contribution-block-title">Select Payment Method</div>
            <div className="payment-method-toggle activation-inline">
              <button
                className={`payment-method-btn ${paymentMethod === 'clover' ? 'active' : ''}`}
                onClick={() => setPaymentMethod('clover')}
                disabled={loading || cloverLoading}
              >
                <span className="clover-icon">☘</span>
                Card
              </button>
              {showAltPaymentMethods && (
                <>
                  {/* PayPal (direct SDK checkout) hidden — payments are
                      currently stuck on the provider side. PayPal QR
                      hidden behind PAYPAL_ENABLED while integration is
                      inactive (see apiConfig.js). */}
                  <button
                    className={`payment-method-btn ${paymentMethod === 'venmo-qr' ? 'active' : ''}`}
                    onClick={() => setPaymentMethod('venmo-qr')}
                    disabled={loading}
                  >
                    <span className="venmo-v">V</span>
                    Venmo QR
                  </button>
                  {PAYPAL_ENABLED && (
                    <button
                      className={`payment-method-btn ${paymentMethod === 'paypal-qr' ? 'active' : ''}`}
                      onClick={() => setPaymentMethod('paypal-qr')}
                      disabled={loading}
                    >
                      <span className="paypal-p">P</span>
                      PayPal QR
                    </button>
                  )}
                </>
              )}
              <button
                type="button"
                className={`payment-expand-btn ${showAltPaymentMethods ? 'open' : ''}`}
                onClick={() => setShowAltPaymentMethods((prev) => !prev)}
                disabled={loading}
                aria-expanded={showAltPaymentMethods}
                aria-label={showAltPaymentMethods ? 'Hide other payment methods' : 'Show more payment methods'}
              >
                {showAltPaymentMethods ? '▾' : '▸'}
                <span className="payment-expand-text">{showAltPaymentMethods ? 'less' : 'more'}</span>
              </button>
            </div>
          </section>

          {/* PayPal Buttons */}
          {paymentMethod === 'paypal' && (
            <div className="contribution-paypal-section">
              {loading && (
                <div className="paypal-processing">
                  <span className="spinner"></span>
                  Processing payment...
                </div>
              )}
              
              {!paypalReady && !paypalFailed && (
                <div className="paypal-loading">
                  <span className="spinner"></span>
                  Loading PayPal...
                </div>
              )}

              {/* Key forces clean React unmount/remount when amount changes */}
              <div 
                key={paypalKey}
                ref={paypalContainerRef} 
                id="contribution-paypal-buttons"
                className={paypalFailed ? 'cp-hidden' : ''}
              />

              {paypalFailed && (
                <div className="paypal-fallback">
                  <p>PayPal is currently unavailable.</p>
                  <button 
                    className="contribution-proceed-btn"
                    onClick={() => {
                      setPaypalFailed(false);
                      setError('');
                      loadPayPalScript().then((loaded) => {
                        if (loaded) renderPayPalButtons();
                      });
                    }}
                  >
                    Retry PayPal
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Venmo QR Code */}
          {paymentMethod === 'venmo-qr' && (
            <div className="qr-code-section">
              <div >
                <div className="qr-code-header">
                  <span className="venmo-v">V</span>
                  <h3>Scan with Venmo</h3>
                </div>
                <div className="qr-code-image">
                  <img 
                    src="/images/VenmoQR.png" 
                    alt="Venmo QR Code"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'block';
                    }}
                  />
                  <div className="qr-code-fallback">
                    <p>📱 Venmo QR Code</p>
                    <p className="qr-username">@username</p>
                    <p className="qr-amount">${getAmount().toFixed(2)}</p>
                  </div>
                </div>
                <div className="qr-code-instructions">
                  <p>1. Open Venmo app</p>
                  <p>2. Scan this QR code</p>
                  <p>3. Send ${getAmount().toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Clover Card Payment (iframe SDK) */}
          {paymentMethod === 'clover' && (
            <div className="clover-checkout-section">
              {cloverSuccess ? (
                <div className="clover-success-msg">
                  <span className="clover-success-icon">✓</span>
                  <p>Payment successful! Thank you for your contribution.</p>
                </div>
              ) : (
                <>
                  <div className="clover-info">
                    <p>Pay securely with credit or debit card.</p>
                  </div>
                  <div className="clover-card-form">
                    <div className="clover-field">
                      <label className="clover-label">Card Number</label>
                      <div id="clover-card-number" className="clover-input-container"></div>
                    </div>
                    <div className="clover-field-row">
                      <div className="clover-field clover-field-half">
                        <label className="clover-label">Expiry</label>
                        <div id="clover-card-date" className="clover-input-container"></div>
                      </div>
                      <div className="clover-field clover-field-half">
                        <label className="clover-label">CVV</label>
                        <div id="clover-card-cvv" className="clover-input-container"></div>
                      </div>
                    </div>
                    <div className="clover-field">
                      <label className="clover-label">ZIP Code</label>
                      <div id="clover-card-zip" className="clover-input-container"></div>
                    </div>
                  </div>
                  {!activationFlow && (
                    <div
                      className={`clover-recurring-toggle ${cloverRecurring ? 'active' : ''}`}
                      onClick={() => setCloverRecurring(prev => !prev)}
                    >
                      <div className={`clover-recurring-switch ${cloverRecurring ? 'on' : ''}`} />
                      <div className="clover-recurring-label">
                        <span>Monthly recurring</span>
                        <span>{cloverRecurring ? `$${getAmount().toFixed(2)}/month auto-charge` : 'One-time payment'}</span>
                      </div>
                    </div>
                  )}
                  <button
                    className="contribution-proceed-btn clover-pay-btn"
                    onClick={handleCloverPay}
                    disabled={cloverLoading || !cloverReady || loading}
                  >
                    {cloverLoading ? (
                      <><span className="spinner"></span> Processing...</>
                    ) : !cloverReady ? (
                      <><span className="spinner"></span> Loading card form...</>
                    ) : (
                      <>{cloverRecurring ? `Pay $${getAmount().toFixed(2)}/mo` : `Pay $${getAmount().toFixed(2)}`}</>
                    )}
                  </button>
                </>
              )}
            </div>
          )}

          {/* PayPal QR Code */}
          {paymentMethod === 'paypal-qr' && (
            <div className="qr-code-section">
              <div >
                <div className="qr-code-header">
                  <span className="paypal-p">P</span>
                  <h3>Scan with PayPal</h3>
                </div>
                <div className="qr-code-image">
                  <img 
                    src="/images/PaypalQR.png" 
                    alt="PayPal QR Code"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'block';
                    }}
                  />
                  <div className="qr-code-fallback">
                    <p>💳 PayPal QR Code</p>
                    <p className="qr-username">@username</p>
                    <p className="qr-amount">${getAmount().toFixed(2)}</p>
                  </div>
                </div>
                <div className="qr-code-instructions">
                  <p>1. Open PayPal app</p>
                  <p>2. Scan this QR code</p>
                  <p>3. Send ${getAmount().toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}

          <button
            className="contribution-remind-btn"
            onClick={handleDismiss}
            disabled={loading}
          >
            Close
          </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContributionPopup;
